import { NgClass } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, inject, input, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { OverlayRef, OverlaysService } from '../overlays';


@Component({
    selector: 'ui-modal',
    imports: [
        NgClass,
    ],
    styles: [
        `
        :host {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
        }

        .modal-backdrop {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1000;
        }

        .modal {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1000;
            padding: 0.5rem;
            padding-bottom: 45px;  /* space for intercom button */
        }

        .modal-dialog {
            margin: auto;
            display: flex;
            flex-direction: column;
            min-height: 0;
            max-height: 100%;
            border: 1px solid rgba(0, 0, 0, 0.2);
            background-clip: padding-box;
            border-radius: 0.3rem;
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }

        @media (max-width: 512px) {
            .modal {
                padding: 0;
            }
            .modal-dialog {
                border-radius: 0;
            }
        }

        .modal-dialog {
            max-width: 500px;
        }

        .modal-dialog.modal-576 {
            max-width: 576px;
        }

        .modal-dialog.modal-640 {
            max-width: 640px;
        }

        .modal-dialog.modal-lg {
            max-width: 800px;
        }

        .modal-dialog.modal-1000 {
            max-width: 1000px;
        }

        .modal-dialog.modal-1100 {
            max-width: 1100px;
        }

        .modal-dialog.modal-1300 {
            max-width: 1300px;
        }
        `,
    ],
    template: `
        <div
            class="modal-backdrop"
            style="--fade-duration: 0.2s"
            [animate.enter]="animation() ? 'fade-enter' : ''"
            [animate.leave]="animation() ? 'fade-leave' : ''">
        </div>
        <div
            class="modal"
            [animate.enter]="animation() ? 'slide-down-enter' : ''"
            [animate.leave]="animation() ? 'slide-down-leave' : ''"
            #modal>
            <div class="modal-dialog" [ngClass]="size() ? 'modal-' + size() : null">
                <ng-content></ng-content>
            </div>
        </div>
    `,
})
export class ModalComponent implements AfterViewInit {

    size = input<null | 'sm' | 'md' | 'lg' | '576' | '640' | '1000' | '1100' | '1300'>(null);

    animation = input(true);

    private modalElementRef = viewChild.required<ElementRef>('modal');

    private overlayRef = inject(OverlayRef<void>);

    private overlaysService = inject(OverlaysService);

    private destroyRef = inject(DestroyRef);

    ngAfterViewInit() {
        fromEvent<MouseEvent>(this.modalElementRef().nativeElement, 'mousedown').pipe(
            mergeMap(start => fromEvent<MouseEvent>(document, 'mouseup').pipe(take(1), map(end => [start, end]))),
            tap(([start, end]) => {
                const clickOutside = start.target === this.modalElementRef().nativeElement;
                if (start.target === end.target && clickOutside && this.overlaysService.isTopmostOverlay(this.overlayRef)) {
                    this.overlayRef.dismiss('click outside');
                }
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }
}
